import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { VendorDistributionData } from '@/hooks/useVendorPerformance';
import { Loader2 } from 'lucide-react';

interface VendorDistributionChartProps {
  data: VendorDistributionData[] | undefined;
  isLoading: boolean;
}

export function VendorDistributionChart({ data, isLoading }: VendorDistributionChartProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Vendor Distribution by Category</CardTitle>
          <CardDescription>Active vendors across job categories</CardDescription>
        </CardHeader>
        <CardContent className="h-[300px] flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Vendor Distribution by Category</CardTitle>
          <CardDescription>Active vendors across job categories</CardDescription>
        </CardHeader>
        <CardContent className="h-[300px] flex items-center justify-center">
          <p className="text-muted-foreground">No vendor distribution data for this period</p>
        </CardContent>
      </Card>
    );
  }

  // Filter out categories with no vendors
  const chartData = data.filter(d => d.biddingVendors > 0 || d.winningVendors > 0);

  if (chartData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Vendor Distribution by Category</CardTitle>
          <CardDescription>Active vendors across job categories</CardDescription>
        </CardHeader>
        <CardContent className="h-[300px] flex items-center justify-center">
          <p className="text-muted-foreground">No vendor activity in any category</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Vendor Distribution by Category</CardTitle>
        <CardDescription>Number of vendors who bid vs. won work per category</CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis
              dataKey="label"
              tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
              tickLine={{ stroke: 'hsl(var(--muted-foreground))' }}
            />
            <YAxis
              tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
              tickLine={{ stroke: 'hsl(var(--muted-foreground))' }}
              allowDecimals={false}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--popover))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
              }}
              formatter={(value: number, name: string) => [
                value,
                name === 'biddingVendors' ? 'Vendors Who Bid' : 'Vendors Who Won',
              ]}
              labelFormatter={(label) => label}
            />
            <Legend
              formatter={(value) => (
                value === 'biddingVendors' ? 'Vendors Who Bid' : 'Vendors Who Won'
              )}
            />
            <Bar
              dataKey="biddingVendors"
              fill="hsl(var(--chart-2))"
              radius={[4, 4, 0, 0]}
              name="biddingVendors"
            />
            <Bar
              dataKey="winningVendors"
              fill="hsl(var(--chart-1))"
              radius={[4, 4, 0, 0]}
              name="winningVendors"
            />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
